import { makeAutoObservable, runInAction } from 'mobx';
import type {
  TAiChatMessageType,
  TAiSessionHydrationMarker,
  TAiSessionInterruptionType,
  TAiSessionMacroState,
  TAiSessionMicroState,
  TGetOwnerAiSessionMessagesResponse,
  TPostCourseMessageResponse,
  TSignalRAiChatMessageData,
  TSignalRProgressEnvelope,
} from '../../services/models/AiSessionModels';
import type { IAiSessionConnection } from '../../services/api/creatorHubConnectionContract';
import type aiSessionAgent from '../../services/api/aiSessionAgent';
import { resolveErrorMessage } from '../../utils/resolveErrorMessage';
import { mapAiSessionCoursePreview, IAiSessionCoursePreview } from './coursePreview';
import type { TCourseViewModel } from '../../services/models/CourseViewModels';

/**
 * The single place that decides what an AI session update *means*.
 *
 * Its entry point, `applyProgressEnvelope`, takes an envelope as a plain value, so the whole receive
 * path can be driven in a test by handing it messages — see
 * `docs/adr/0001-ai-session-receive-layer-connection-store-split.md`. That is why this module must
 * never import SignalR: it holds a connection through the port type only, and the wiring that feeds
 * it envelopes lives outside both modules, in `createAiSessionStore`.
 *
 * Every decision here reads the **macro state** the backend sends. Nothing is decided by matching
 * text in a message or in a status description: a reworded backend message must not be able to
 * change what the author sees.
 *
 * Three things that can go wrong are held apart, because the author does something different about
 * each: **transport health** (`transportStatus`) is the socket, and the only surface that may say
 * the connection was lost; **session interruption** (`isWorkerInterrupted`, `interruptionReason`)
 * is a run that stopped short, resumable through `continueSession`; and **request failure**
 * (`requestFailureMessage`) is a call the server rejected, where what is retried is the request. A
 * failure never crosses from one to another.
 */

export type TAiSessionChatRole = 'user' | 'assistant';

/**
 * Transport health: what the socket itself is doing, and the only surface allowed to say the
 * connection was lost.
 *
 * Written from the socket's own lifecycle and nothing else — the transport's reports, a handshake
 * that completed, and a stop we asked for. Never inferred from a rejected call: a failure that
 * arrives *over* the socket is proof the socket works, and a rejected request says nothing about
 * it either. `idle` is a wizard that has opened nothing yet, and the state a stopped connection
 * goes back to: a socket we asked to close is not a socket we lost.
 */
export type TAiSessionTransportStatus = 'idle' | 'connected' | 'reconnecting' | 'disconnected';

export interface IAiSessionChatMessage {
  id: string;
  role: TAiSessionChatRole;
  content: string;
}

/**
 * Where a message goes, and everything that follows from going there.
 *
 * A chat message needs a speaker; the other two destinations do not, so the speaker rides on the
 * chat case rather than being worked out a second time at the point of use.
 */
export type TAiSessionMessageRouting = { destination: 'chat'; role: TAiSessionChatRole } | { destination: 'status' } | { destination: 'developerBuffer' };

/**
 * A run that stopped short of its next macro state.
 *
 * `reason` is the backend's own name for why, or null when the interruption was reported by the flag
 * on the system state payload, which says the worker died without saying why.
 */
export interface IAiSessionInterruption {
  reason: TAiSessionInterruptionType | null;
}

/** One line of the developer buffer — everything the author is deliberately not shown. */
export interface IAiSessionDeveloperBufferEntry {
  id: string;
  type: TAiChatMessageType;
  content: string;
}

/**
 * One message, in the one log every message lives in, keyed by identity.
 *
 * Where it is shown is not stored on it: the routing table below answers that from the type, at the
 * point the chat, the developer buffer and the status line are read. Holding one log is what lets a
 * re-fetched history be merged in one pass instead of three.
 */
interface IAiSessionLogEntry {
  id: string;
  type: TAiChatMessageType;
  content: string;
  /** Set on a status notice the run has moved past, so the line goes quiet without losing what it said. */
  isDismissed?: boolean;
}

export interface IAiSessionStoreDependencies {
  connection: IAiSessionConnection;
  agent: typeof aiSessionAgent;
  getCoursePages: (courseId: number) => Promise<TCourseViewModel>;
}

/** The macro states that mean generation is under way — every state that is neither the interview nor the end. */
const GENERATING_MACRO_STATES: TAiSessionMacroState[] = ['generatingCourseOutline', 'ingestingCourseOutline', 'generatingPageContent', 'ingestingPageContent', 'finalizingCourse'];

/**
 * The macro states worth re-reading the course page tree on entering.
 *
 * Pages are written as the run assembles the course, so finalizing is the first state in which
 * there is something to show, and completion is the state the last of them landed in. Nothing else
 * on the feed can have changed the tree: a chat message is a conversation turn, and a micro state
 * is a step inside a stage the tree does not follow. A long run that fetched on every push issued
 * dozens of requests for a tree that had not moved.
 */
const PAGE_TREE_CHANGING_MACRO_STATES: TAiSessionMacroState[] = ['finalizingCourse', 'completed'];

/**
 * Whether an update moved the run into one of them. Entering is the question, not being there: a
 * run sits in finalizing across many pushes, and the tree is worth one look, not one per push.
 */
const entersPageTreeChangingState = (before: TAiSessionMacroState | null, after: TAiSessionMacroState | null): boolean =>
  after !== null && after !== before && PAGE_TREE_CHANGING_MACRO_STATES.includes(after);

/**
 * Where each kind of message belongs. The author's chat is the conversation and nothing else.
 *
 * One table, read by both the live feed and a re-fetched history, so the two paths cannot drift:
 * a message routes the same way whichever of them delivered it. `undefined` is the message type
 * enum's unset default and has no author-facing meaning, so it goes where anything the author must
 * not read goes.
 */
const CHAT_MESSAGE_ROUTING: Record<TAiChatMessageType, TAiSessionMessageRouting> = {
  ownerMessage: { destination: 'chat', role: 'user' },
  aiResponse: { destination: 'chat', role: 'assistant' },
  systemMessage: { destination: 'status' },
  systemLog: { destination: 'developerBuffer' },
  courseGenerationJson: { destination: 'developerBuffer' },
  messageSummary: { destination: 'developerBuffer' },
  undefined: { destination: 'developerBuffer' },
};

/**
 * Where a message type the contract does not model goes. Unreachable through the types, and there
 * for the day the backend adds an enum member: an unrecognised message is one the author must not
 * be shown, so it goes to the buffer rather than crashing the receive path or reaching the chat.
 */
const UNMODELLED_MESSAGE_ROUTING: TAiSessionMessageRouting = { destination: 'developerBuffer' };

const routingFor = (messageType: TAiChatMessageType): TAiSessionMessageRouting => CHAT_MESSAGE_ROUTING[messageType] ?? UNMODELLED_MESSAGE_ROUTING;

/**
 * The hub's own word for the envelope it answers a subscribe with, riding the field a micro state
 * would otherwise ride. No worker turn is under way when it arrives, so it must not read as one:
 * what it is for is the interruption flag beside it, which says whether the worker died mid-run.
 */
const HYDRATION_STATE_KEY: TAiSessionHydrationMarker = 'connectionRestored';

/**
 * The reason to record for a failure envelope.
 *
 * A failure envelope names the interruption; one that names nothing has interrupted the session
 * just the same, and the backend enum has `unknown` for exactly that. `none` is the enum's unset
 * default and never reaches the wire, so it is read as no name rather than as a reason.
 */
const reasonNamedBy = (reactName: TAiSessionInterruptionType | null): TAiSessionInterruptionType => (reactName && reactName !== 'none' ? reactName : 'unknown');

/**
 * Message identity, and the two kinds of it — see
 * `docs/adr/0002-chat-messages-keyed-by-server-assigned-message-id.md`.
 *
 * A **persisted message** is keyed by the id the backend assigned it, so a re-fetched history
 * matches the copy already on screen rather than adding a second one. A **transient message** is
 * keyed by a client-assigned id. Which prefix an entry carries is the only thing that says whether
 * a history message can still claim it.
 */
const TRANSIENT_ID_PREFIX = 'transient-';
const PERSISTED_ID_PREFIX = 'persisted-';

const persistedMessageId = (ownerAiSessionMessageId: number): string => `${PERSISTED_ID_PREFIX}${ownerAiSessionMessageId}`;

const isTransientId = (id: string): boolean => id.startsWith(TRANSIENT_ID_PREFIX);

export default class AiSessionStore {
  aiSessionId: number | null = null;
  courseId: number | null = null;

  /** The stage the session has actually reached. Null until an update or a resumed session says. */
  macroState: TAiSessionMacroState | null = null;
  /**
   * What the worker is doing inside the current macro state. Display-only telemetry: nothing here
   * or anywhere else reads it to decide what the session has accomplished, and no run of micro
   * states may move the progress list. Null whenever no worker turn is under way.
   */
  microState: TAiSessionMicroState | null = null;

  /** Transport health — see `TAiSessionTransportStatus` for what may write it. */
  transportStatus: TAiSessionTransportStatus = 'idle';

  coursePreview: IAiSessionCoursePreview | null = null;

  isSending = false;
  isAwaitingReply = false;
  /**
   * The request failure surface: a call of ours the server rejected. What the author retries is the
   * request, not the session — so this never says the connection went and never says the run
   * stopped, and neither of those ever lands here.
   */
  requestFailureMessage: string | null = null;

  /**
   * Every message the session has produced, in the order it arrived, whichever of the three places
   * it is shown in. One log is what gives every message an identity to be merged by — a status line
   * held as a single current value has none, and a re-fetch would put an already-seen notice back
   * over a newer one that only ever arrived live.
   */
  private messageLog: IAiSessionLogEntry[] = [];

  /**
   * The session interruption surface: the run stopped short of its next macro state, or null when
   * it did not. Held as one value so a reason cannot exist without an interruption to belong to.
   */
  private interruption: IAiSessionInterruption | null = null;

  private deps: IAiSessionStoreDependencies;
  /**
   * The session we asked the hub to join us to, which is not the same as the one we are looking at.
   *
   * Written by `subscribeToSession` and nowhere else. A send joins a group too, but the API does
   * that on our behalf and stopping the connection gives the membership up, so there is nothing for
   * the send path to record here and no unsubscribe it has to arrange.
   */
  private subscribedSessionId: number | null = null;
  private transientMessageSequence = 0;
  private isRefreshingPages = false;
  private queuedPageRefresh = false;

  constructor(dependencies: IAiSessionStoreDependencies) {
    this.deps = dependencies;
    makeAutoObservable<this, 'deps'>(this, { deps: false });
  }

  // --- The seam: everything the socket delivers arrives here ---

  applyProgressEnvelope = (envelope: TSignalRProgressEnvelope) => {
    // A failure envelope is a session interruption, not a transport failure: it arrived over a
    // socket that is plainly working, and the session it names is still there to continue from.
    // The envelope's own `errorMessage` is admin-console prose and is not read here; the reason on
    // `reactName` is what the author's wording and support's diagnosis are both driven from.
    if (envelope.success !== true) {
      this.reportInterruption(reasonNamedBy(envelope.reactName));
      return;
    }

    const update = envelope.data;
    if (!update) return;

    if (update.aiSessionId) this.aiSessionId = update.aiSessionId;

    // Read before the state is adopted, so the page-tree question below has a before to ask about.
    const macroStateBefore = this.macroState;
    if (update.currentState) this.adoptMacroState(update.currentState);

    // Telemetry, both of them. The micro state is kept so the author can see the run is still
    // moving, and the interruption flag so the session can present as interrupted with a way to
    // continue. The socket stays open either way, and neither is allowed to say the run got
    // anywhere: the macro state above has already had the only word on that.
    if (update.systemStateData) {
      // The hub answers a subscribe with a hydration envelope, which rides its own marker on this
      // field rather than a micro state. No worker turn is under way when it arrives, so it clears
      // the micro state instead of reporting one; its interruption flag is read like any other.
      const workerStateKey = update.systemStateData.aiSessionWorkerStateTypeKey;
      this.microState = workerStateKey === HYDRATION_STATE_KEY ? null : workerStateKey;

      // The flag says the worker died, not why, so it names no reason — and a reason already known
      // from a failure envelope is kept rather than blanked by the resume that follows it.
      if (update.systemStateData.isInterrupted) this.reportInterruption(null);
      else this.clearInterruption();
    }

    if (update.chatMessageData) this.applyChatMessage(update.chatMessageData);

    if (entersPageTreeChangingState(macroStateBefore, this.macroState)) void this.refreshCoursePreview();
  };

  // --- The transport reports its own health here. Nothing but the socket's lifecycle writes it. ---

  /** The socket dropped and SignalR is trying it again by itself. Not yet a lost connection. */
  reportTransportReconnecting = () => {
    this.transportStatus = 'reconnecting';
  };

  /** The socket is back. The group membership did not survive it, so the session is rejoined. */
  reportTransportRestored = (): Promise<void> => {
    this.transportStatus = 'connected';

    return this.resubscribe();
  };

  /**
   * The socket closed and is not coming back on its own. The one thing in the client that means
   * "connection lost" — which is why a close we asked for never reaches here.
   */
  reportTransportLost = () => {
    this.transportStatus = 'disconnected';
  };

  // --- The three views of the log. The routing table decides which entries each one holds. ---

  /** The conversation, and nothing else. */
  get messages(): IAiSessionChatMessage[] {
    return this.messageLog.reduce<IAiSessionChatMessage[]>((chat, entry) => {
      const routing = routingFor(entry.type);
      if (routing.destination === 'chat') chat.push({ id: entry.id, role: routing.role, content: entry.content });

      return chat;
    }, []);
  }

  /**
   * Everything routed away from the chat: log lines, generated course JSON and conversation
   * summaries. Read only behind the technical-errors toggle, so a developer can work out what a
   * session did without a database query.
   */
  get developerBuffer(): IAiSessionDeveloperBufferEntry[] {
    return this.messageLog.filter((entry) => routingFor(entry.type).destination === 'developerBuffer');
  }

  /** The one line of notice the author reads: the latest one, unless the run has moved past it. */
  get statusNotice(): string | null {
    const latest = this.latestStatusNotice;

    return latest && !latest.isDismissed ? latest.content : null;
  }

  private get latestStatusNotice(): IAiSessionLogEntry | undefined {
    return this.messageLog.filter((entry) => routingFor(entry.type).destination === 'status').pop();
  }

  /**
   * Whether the run stopped short. An interrupted session is resumable and is not a failed one: the
   * socket stays up, and `continueSession` picks the run up where it stopped.
   */
  get isWorkerInterrupted(): boolean {
    return this.interruption !== null;
  }

  /**
   * The backend's own name for why the run stopped, so support can tell a timeout from a refusal
   * from a database lock. Null when the only thing that reported the interruption was the flag on
   * the system state payload, which says the worker died without saying why.
   */
  get interruptionReason(): TAiSessionInterruptionType | null {
    return this.interruption?.reason ?? null;
  }

  // --- Derived session state. All of it reads macro state. ---

  get isInterviewing(): boolean {
    return this.macroState === 'interviewing';
  }

  get isGenerating(): boolean {
    return this.macroState !== null && GENERATING_MACRO_STATES.includes(this.macroState);
  }

  get isComplete(): boolean {
    return this.macroState === 'completed';
  }

  /** The course the run produced, once there is one to open. */
  get generatedCourseId(): number | undefined {
    return this.coursePreview?.courseId;
  }

  // --- Session lifecycle ---

  /**
   * Resumes an existing session: subscribe for updates, then merge in what has already happened.
   *
   * Both halves are a resume concern only. The send path subscribes to nothing — the API does that
   * for it — and re-fetches nothing, because the log already holds the message the author just sent
   * and the backend never echoes it back.
   */
  attachToSession = async (aiSessionId: number, courseId: number | null) => {
    runInAction(() => {
      // Only a different session starts from nothing. Re-attaching to the one already on screen
      // merges into what is there, the same as a reconnect does.
      if (this.aiSessionId !== aiSessionId) this.clearMessageLog();

      this.aiSessionId = aiSessionId;
      if (courseId !== null) this.courseId = courseId;
    });

    // A resume that cannot reach the hub is an error surface, not a rejected promise: the wizard
    // opens either way, and a session that has already finished must not be a dead end.
    await this.runGuarded(async () => {
      await this.subscribeToSession(aiSessionId);
      await this.mergeSessionHistory(aiSessionId);
    });

    await this.refreshCoursePreview();
  };

  /** The socket came back on its own; the group membership did not, so ask for it again. */
  resubscribe = (): Promise<void> => {
    const aiSessionId = this.aiSessionId;
    if (aiSessionId === null) return Promise.resolve();

    this.subscribedSessionId = null;

    return this.runGuarded(async () => {
      await this.subscribeToSession(aiSessionId);
      await this.mergeSessionHistory(aiSessionId);
    });
  };

  /** Leaves the session running on the server and lets go of it here. */
  detach = async () => {
    const aiSessionId = this.subscribedSessionId;
    this.clearSessionState();

    if (aiSessionId !== null) await this.deps.connection.unsubscribe(aiSessionId).catch(() => undefined);
    await this.deps.connection.stop();
  };

  /**
   * Sends the author's message, creating the session and opening the socket if this is the first.
   *
   * Nothing is opened before this: an author who opens the wizard and changes their mind leaves no
   * socket and no session behind. Rejects when the message did not go, so the caller can put the
   * typed text back in the box.
   */
  sendMessage = async (prompt: string) => {
    if (this.isSending || prompt.length < 1) return;

    // The backend stores the author's own message but never echoes it, so it is rendered here and
    // there is nothing to reconcile against later. It goes through the routing table like any other
    // message, so what the author sees of their own message does not depend on how it reached them.
    const ownMessageId = this.nextTransientMessageId();
    this.addMessage('ownerMessage', prompt, ownMessageId);
    this.isSending = true;
    this.requestFailureMessage = null;

    let response: TPostCourseMessageResponse;
    try {
      // The handshake first, and its id on the post: the API joins this connection to the session's
      // broadcast group before it queues the job, so the feed is live from the earliest update
      // rather than joined halfway through. Never post without an id — the job would run with
      // nobody listening, and several of the messages it emits are never persisted.
      const signalRConnectionId = await this.startConnection();

      response = await this.deps.agent.Ai.postCourseMessage({
        aiPrompt: prompt,
        debugMode: false,
        ownerAiSessionId: this.aiSessionId,
        courseId: this.courseId,
        signalRConnectionId,
      });
    } catch (error) {
      runInAction(() => {
        this.removeMessage(ownMessageId);
        this.requestFailureMessage = resolveErrorMessage(error);
        this.isSending = false;
      });
      throw error;
    }

    runInAction(() => {
      this.aiSessionId = response.aiSessionId;
      this.courseId = response.courseId;
      this.isAwaitingReply = response.jobAccepted;
      if (response.jobAccepted) this.dismissStatusNotices();
      else this.addMessage('systemMessage', response.statusMessage || 'The AI is still processing a previous message.', this.nextTransientMessageId());
      this.isSending = false;
    });
  };

  /**
   * The continue affordance: picks the interrupted run up where it stopped, without a new message.
   *
   * A rejected continue is a request failure and leaves the session interrupted, so the affordance
   * is still there to try again.
   */
  continueSession = async () => {
    const aiSessionId = this.aiSessionId;
    if (aiSessionId === null || this.isSending) return;

    this.requestFailureMessage = null;

    try {
      await this.deps.agent.Ai.continueInterruptedAiSession({ aiSessionId });
      runInAction(() => {
        this.clearInterruption();
        this.isAwaitingReply = true;
      });
    } catch (error) {
      runInAction(() => {
        this.requestFailureMessage = resolveErrorMessage(error);
      });
    }
  };

  /** Deletes the session on the server and empties the wizard, so the author starts over. */
  closeSession = async () => {
    const aiSessionId = this.aiSessionId;

    if (aiSessionId !== null) await this.deps.agent.Ai.closeOwnerAiSession({ aiSessionId });
    await this.detach();
  };

  // --- Internals ---

  /**
   * The run stopped short of its next macro state.
   *
   * The socket is left alone: an interruption is not a transport failure, and the session is still
   * there to continue from. Nothing is on its way any more either, so the author is told the run
   * stopped instead of being left watching the assistant think.
   */
  private reportInterruption = (reason: TAiSessionInterruptionType | null) => {
    // A reason already known is kept: the flag on the system state payload reports the interruption
    // again on every resume without ever naming it, and would otherwise take the name away.
    this.interruption = { reason: reason ?? this.interruptionReason };
    this.isAwaitingReply = false;
  };

  private clearInterruption = () => {
    this.interruption = null;
  };

  private adoptMacroState = (macroState: TAiSessionMacroState) => {
    // A micro state belongs to the stage that was running when it arrived, so leaving that stage
    // leaves it behind. An envelope carrying both a new stage and a micro state still ends with the
    // micro state, because the stage is adopted first.
    if (macroState !== this.macroState) this.microState = null;

    this.macroState = macroState;

    // Completion ends the run, so nothing is still on its way.
    if (macroState === 'completed') {
      this.isAwaitingReply = false;
      this.dismissStatusNotices();
    }
  };

  private applyChatMessage = (chatMessage: TSignalRAiChatMessageData) => {
    this.addMessage(chatMessage.ownerAiSessionMessageTypeKey, chatMessage.messageText, this.nextTransientMessageId());

    // The AI answering ends the worker's turn, so there is no longer anything it is in the middle of.
    // Interviewing stays on one macro state across every turn, so nothing else would clear the last
    // micro state and the status line would keep reporting work on an idle session.
    if (chatMessage.ownerAiSessionMessageTypeKey === 'aiResponse') {
      this.isAwaitingReply = false;
      this.microState = null;
    }
  };

  /**
   * Adds a message to the log, wherever the routing table will go on to show it. The one way in, so
   * live updates and re-fetched history cannot end up shown in two different places.
   */
  private addMessage = (messageType: TAiChatMessageType, content: string, id: string, isDismissed = false) => {
    this.messageLog = [...this.messageLog, { id, type: messageType, content, isDismissed }];
  };

  /** Nothing is on its way any more, so the line goes quiet — without losing what it has said. */
  private dismissStatusNotices = () => {
    this.messageLog = this.messageLog.map((entry) => (routingFor(entry.type).destination === 'status' && !entry.isDismissed ? { ...entry, isDismissed: true } : entry));
  };

  private nextTransientMessageId = (): string => `${TRANSIENT_ID_PREFIX}${this.transientMessageSequence++}`;

  private removeMessage = (messageId: string) => {
    this.messageLog = this.messageLog.filter((entry) => entry.id !== messageId);
  };

  /**
   * Starts the socket and records that it is up. The one place a successful handshake is turned
   * into transport health, so the send path and the resume path cannot disagree about it.
   */
  private startConnection = async (): Promise<string> => {
    const connectionId = await this.deps.connection.start();

    runInAction(() => {
      this.transportStatus = 'connected';
    });

    return connectionId;
  };

  /**
   * Joins the session's broadcast group. Resume and reconnect only — a send has no use for it,
   * because the API joins the connection itself as part of accepting the post.
   */
  private subscribeToSession = async (aiSessionId: number) => {
    if (this.subscribedSessionId === aiSessionId) return;

    await this.startConnection();
    await this.deps.connection.subscribe(aiSessionId);
    this.subscribedSessionId = aiSessionId;
  };

  /**
   * Re-fetches the conversation and merges it into what is already on screen.
   *
   * Merging rather than replacing is the whole of ADR-0002. A replace would take back every
   * transient message — a live-only notice, a buffer line the backend never stored — because they
   * are in no history and never will be. An append would show every persisted message twice.
   *
   * History carries the same message type key the live feed sends, so it goes through the same
   * routing table. The Gemini `role` field is not read: it distinguishes speaker only, and cannot
   * tell an AI reply from a user-facing notice from a log line.
   */
  private mergeSessionHistory = async (aiSessionId: number) => {
    const history = await this.deps.agent.Ai.getOwnerAiSessionMessages({ aiSessionId });

    runInAction(() => history.forEach(this.mergeHistoryMessage));
  };

  private mergeHistoryMessage = (message: TGetOwnerAiSessionMessagesResponse) => {
    const messageType = message.ownerAiSessionMessageTypeKey;
    const content = message.messageText;
    const id = persistedMessageId(message.ownerAiSessionMessageId);

    // The id is the identity, so a message the log already holds is one this merge has nothing to
    // do with — that is the whole of the no-duplication guarantee across repeated re-fetches.
    if (this.messageLog.some((entry) => entry.id === id)) return;

    // Otherwise it may still be on screen under a client-assigned id: a pushed message carries no
    // id of its own, so one that was both pushed and stored arrives here for the first time with
    // the server's. Re-keying in place is what keeps it where the author last saw it, and keeps it
    // from being shown a second time.
    const twinIndex = this.messageLog.findIndex((entry) => isTransientId(entry.id) && entry.type === messageType && entry.content === content);
    if (twinIndex !== -1) {
      this.messageLog = this.messageLog.map((entry, index) => (index === twinIndex ? { ...entry, id } : entry));
      return;
    }

    // Genuinely unseen: the first fetch of a resumed session, or a message that arrived while the
    // connection was down. A re-fetch never wakes a status line the run has already moved past, so
    // an unseen notice arrives as quiet as the line it lands on.
    this.addMessage(messageType, content, id, this.latestStatusNotice?.isDismissed ?? false);
  };

  private refreshCoursePreview = async () => {
    const courseId = this.courseId;
    if (courseId === null) return;

    // Two fetches can still overlap — a completion that lands while the attach fetch is in flight —
    // so the second waits for the first and then runs, rather than being dropped. Dropping it would
    // leave the sidebar on a tree read before the last pages were written.
    if (this.isRefreshingPages) {
      this.queuedPageRefresh = true;
      return;
    }

    this.isRefreshingPages = true;

    try {
      const course = await this.deps.getCoursePages(courseId);
      runInAction(() => {
        this.coursePreview = mapAiSessionCoursePreview(course);
      });
    } catch {
      // Not worth an error surface of its own: the sidebar keeps the tree it last read rather than
      // emptying. There are only three of these now, so a rejected one is not retried — a fetch that
      // fails on completion leaves the sidebar a stage behind until the wizard is opened again.
    } finally {
      runInAction(() => {
        this.isRefreshingPages = false;
      });
    }

    if (this.queuedPageRefresh) {
      this.queuedPageRefresh = false;
      await this.refreshCoursePreview();
    }
  };

  /** One session's conversation, gone — every view of it with it. */
  private clearMessageLog = () => {
    this.messageLog = [];
    this.transientMessageSequence = 0;
  };

  private clearSessionState = () => {
    this.aiSessionId = null;
    this.courseId = null;
    this.subscribedSessionId = null;
    this.macroState = null;
    this.microState = null;
    this.clearInterruption();
    this.coursePreview = null;
    this.isAwaitingReply = false;
    this.requestFailureMessage = null;
    // The socket goes down with the session, but we asked for that: it is idle again, not lost.
    this.transportStatus = 'idle';
    this.clearMessageLog();
  };

  /**
   * Runs a resume step whose failure the author must not be stopped by, and puts it on the request
   * surface. A resume is our request; that its subject is the session does not make it a session
   * interruption, and that one of its steps touches the socket does not make it a lost connection —
   * only the transport reports that.
   */
  private runGuarded = async (task: () => Promise<void>) => {
    try {
      await task();
    } catch (error) {
      runInAction(() => {
        this.requestFailureMessage = resolveErrorMessage(error);
      });
    }
  };
}
